import { IconButton } from "@itk/mui-components";
import { Grid, InputAdornment, Skeleton, Stack, TextField } from "@mui/material";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { ComponentType, useEffect, useMemo, useState } from "react";
import { DekoxIcon, ViewMode } from "src/components";
import { ITEM_CARD_IMAGE_HEIGHT } from "../../add-edit-configuration-dialog-types";
import { ItemCard } from "../item-card";
import { connect } from "react-redux";
import { addEditConfigurationDialogActions } from "../../add-edit-configuration-dialog-store";
import { RootState } from "src/store";
import { useParams } from "react-router";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";

const mapStateToProps = (state: RootState) => ({
  productsState: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.productsState,
  productPictures: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.productPictures,
  formData: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.formData
});

const ownActions = {
  setAddEditConfigurationDialog: addEditConfigurationDialogActions.setAddEditConfigurationDialog,
  getProductsRequest: addEditConfigurationDialogActions.getProductsRequest,
  setFormData: addEditConfigurationDialogActions.setFormData
};

type OwnProps = { onSave?: (productId: string) => void };
type Props = typeof ownActions & ReturnType<typeof mapStateToProps> & OwnProps;

const SelectProductBase = (props: Props) => {
  const { setAddEditConfigurationDialog, getProductsRequest, productsState, formData, setFormData, productPictures, onSave } = props;

  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();

  const { projectId } = useParams<{ projectId: string }>();

  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!projectId || !formData?.productsFilter?.systemPackageId) return;
    getProductsRequest(projectId, { ...(formData.productsFilter || {}) });
  }, [formData.productsFilter?.systemPackageId]);

  const products = useMemo(() => {
    if (productsState.loading === "has-loaded") {
      return productsState.data.sort().filter(x => {
        return (
          x.name?.lowerCaseIncludes(searchText) ||
          x.productNumber?.lowerCaseIncludes(searchText) ||
          x.description2?.lowerCaseIncludes(searchText) ||
          x.summary?.lowerCaseIncludes(searchText) ||
          x.productTypeName?.lowerCaseIncludes(searchText)
        );
      });
    }
    return [];
  }, [searchText, productsState]);

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" gap={2}>
        <TextField
          autoFocus
          fullWidth
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder={genericTranslationsT.search}
          autoComplete="search"
          sx={{
            "& .MuiInputBase-input": {
              padding: 0
            },
            "& .MuiInputBase-root .MuiInputBase-input": {
              height: 40
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <DekoxIcon icon="search" color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchText && (
                    <IconButton k={genericTranslationsK.dynamicValue} onClick={() => setSearchText("")}>
                      <DekoxIcon fontSize="small" icon="close" />
                    </IconButton>
                  )}
                </InputAdornment>
              )
            }
          }}
        />
        <ViewMode value={formData?.viewMode || "FULL"} onChange={value => setFormData({ viewMode: value })} />
      </Stack>
      <Grid container spacing={2}>
        {productsState.loading === "is-loading" && (
          <>
            {Array(4)
              .fill(undefined)
              .map((_, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Skeleton height={formData.viewMode === "FULL" ? ITEM_CARD_IMAGE_HEIGHT : 62} />
                </Grid>
              ))}
          </>
        )}
        {productsState.loading === "has-loaded" && products.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Stack alignItems="center" py={4}>
              {configuratorPageT.noProductsFound}
            </Stack>
          </Grid>
        )}
        {productsState.loading === "has-loaded" && (
          <>
            {products.map(product => {
              const picture = productPictures.find(productPicture => productPicture.productPictureId === product.primaryProductPictureId)?.picture;

              return (
                <Grid key={product.productId} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <ItemCard
                    key={product.productId}
                    id={product.productId}
                    name={product.name}
                    productNumber={product.productNumber}
                    type={product.productTypeName}
                    image={picture}
                    isSelected={formData.selectedProductId === product.productId}
                    onSelect={() => {
                      setFormData({ selectedProductId: product.productId });
                    }}
                    onDoubleClick={() => onSave?.(product.productId)}
                    onInfoIconClick={() => {
                      setAddEditConfigurationDialog({ step: "PRODUCT-DETAILS", productId: product.productId });
                    }}
                    isMinimal={formData.viewMode === "MINIMAL"}
                  />
                </Grid>
              );
            })}
          </>
        )}
      </Grid>
    </Stack>
  );
};

export const SelectProduct = connect(mapStateToProps, ownActions)(SelectProductBase) as unknown as ComponentType<OwnProps>;
